import i18n from "i18next";
import { initReactI18next } from "react-i18next";

type Bundle = Record<string, unknown>;

// English is the fallback for every language, so it ships in the main bundle; every other language is fetched on demand (one chunk per language).
const english = import.meta.glob<Bundle>("../locales/en/*.json", { eager: true, import: "default" });
const others = import.meta.glob<Bundle>(["../locales/*/*.json", "!../locales/en/*.json"], { import: "default" });

const nsOf = (path: string) => path.match(/\/([^/]+)\.json$/)?.[1];
const resources: Record<string, Record<string, Bundle>> = { en: {} };
for (const [path, content] of Object.entries(english)) {
  const ns = nsOf(path);
  if (ns) resources.en[ns] = content;
}

const loading = new Map<string, Promise<void>>();

/** Fetches every namespace of a language once; later calls reuse the same promise. */
export function loadLanguage(lng: string): Promise<void> {
  if (lng === "en") return Promise.resolve();
  let p = loading.get(lng);
  if (!p) {
    const entries = Object.entries(others).filter(([path]) => path.includes(`/locales/${lng}/`));
    p = Promise.all(
      entries.map(async ([path, load]) => {
        const ns = nsOf(path);
        if (ns) i18n.addResourceBundle(lng, ns, await load(), true, true);
      })
    ).then(() => undefined);
    p.catch(() => loading.delete(lng));
    loading.set(lng, p);
  }
  return p;
}

const saved = localStorage.getItem("language") || "en";

i18n.use(initReactI18next).init({
  resources,
  lng: "en",
  fallbackLng: "en",
  defaultNS: "common",
  interpolation: {
    escapeValue: false,
  },
});

// Any language switch (header menu, institution setting) loads that language's files first, so text never flashes back to English.
const switchLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async (lng, callback) => {
  if (lng) await loadLanguage(lng).catch(() => undefined);
  return switchLanguage(lng, callback);
};

/** Resolves once the language this browser last used is ready; render the app after it. */
export const i18nReady: Promise<unknown> = i18n.changeLanguage(saved).catch(() => undefined);

export default i18n;


const FONT_URL = "https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap";
const syncLanguage = (lng: string) => {
  document.documentElement.lang = lng;
  if (lng === "hi" && !document.getElementById("font-devanagari")) {
    const link = document.createElement("link");
    link.id = "font-devanagari";
    link.rel = "stylesheet";
    link.href = FONT_URL;
    document.head.appendChild(link);
  }
};
syncLanguage(i18n.language);
i18n.on("languageChanged", syncLanguage);
